//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import { FilterMatchState } from '@models/FilterMatchState';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import InstagramBusinessAccountId from '@models/InstagramBusinessAccountId';
import InstagramUsername from '@models/InstagramUsername';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';

export interface IInstagramShopsEligibilityResponse {
  isShopTagsEligible: boolean;
}

export interface IInstagramShopsCatalogResponse {
  catalogs: IInstagramShopsCatalog[];
}

export interface IInstagramShopsCatalog {
  catalogId: string;
  catalogName?: string;
  shopName?: string;
  productCount?: number;
}

export interface IInstagramShopsCatalogSearchRequest {
  catalogId: string;
  searchQuery?: string;
}

export interface IInstagramShopsCatalogSearchResponse {
  products: IInstagramShopsProduct[];
}

export interface IInstagramShopsProduct {
  imageUrl?: string;
  isCheckoutFlow?: boolean;
  merchantId?: string;
  productId: string;
  productName?: string;
  productVariants: IInstagramShopsProductVariant[];
  retailerId?: string;
  reviewStatus?: string;
}

export interface IInstagramShopsProductVariant {
  productId: string;
  variantName?: string;
}

export interface IInstagramShopsItemsSearchRequest {
  businessUserId: InstagramBusinessAccountId;
  mentionedUsername: InstagramUsername;
  itemFilter: InstagramShopsItemFilter;
  matchState: FilterMatchState;
  startTime: DateString;
  endTime: DateString;
  sortKey: UniversalSearchSortKey;
  offset?: number;
  limit?: number;
}

export interface IInstagramShopsAutocompleteRequest {
  businessUserId: InstagramBusinessAccountId;
  mentionedUsername: InstagramUsername;
  itemFilter: InstagramShopsItemFilter;
  searchTerm: string;
  matchState: FilterMatchState;
  startTime: DateString;
  endTime: DateString;
}

export interface IInstagramShopsItemsSearchResponse {
  data: IInstagramShopsSearchResult;
}

export interface IInstagramShopsSearchResult {
  items: IMlsMediaItem[];
  totalCount: number;
}

export interface IInstagramShopsFeatureRequest {
  businessUserId: InstagramBusinessAccountId;
  cuid: Cuid;
  productIds: string[];
}

export interface IInstagramShopsItemsRequest {
  cuids: Cuid[];
}

export interface IGetInstagramShopsItemsByCuidResponse {
  data: IMlsMediaItem[];
}

export enum InstagramShopsItemFilter {
  AllMedia = 'AllMedia', // All Media
  AllApprovedMedia = 'AllApprovedMedia', // All owned/approved media regardless of product tags and prior IG Shops features
  HasProductsNotFeatured = 'HasProductsNotFeatured', // Media already product tagged within Social Commerce and not yet featured on IG Shops
  IsApprovedNotFeatured = 'IsApprovedNotFeatured', // Rights approved media not yet featured on IG Shops
  IsFeatured = 'IsFeatured', // Media already featured on IG Shops
  NoProductsNotFeatured = 'NoProductsNotFeatured', // Media not yet product tagged within Social Commerce and not yet featured on IG Shops
}

export interface IGetShopTagResponse {
  shopTag: IInstagramShopsProduct;
}

export interface IGetShopTagsRequest {
  shopTagIds: string[];
}

/**
 * Batch get shop tags response
 *
 * failedShopTags is a map from `shopTagId` to `errorMessage`
 */
export interface IGetShopTagsResponse {
  successfulShopTags: IInstagramShopsProduct[];
  failedShopTags: Record<string, string>;
}
